import { isIOS } from '@vindr/utils/mobile';
import { Table as AntTable, ConfigProvider, Empty, TableProps } from 'antd';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import useCalcTableScroll from '@vindr/utils/hooks/useCalcTableScroll';
import PaginationTable from './PaginationTable';
import PopupContextMenu from '../ui/PopupContextMenu';
import { useThemeContext } from './ThemeProvider';
import { MenuProps } from '../ui/PopupContextMenu/PopupContextMenu';

export interface VindrTableProps<T = Record<string, unknown>>
  extends Omit<TableProps<T>, 'pagination'> {
  columns: TableProps<T>['columns'];
  scroll?: TableProps<T>['scroll'] & { minY?: number };
  rowKey?: string | ((record: T, idx: number) => string);
  loading?: boolean;
  pagination?: false | null | PaginationProps;
  contextMenu?: MenuProps | false;
  count?: number;
  onTableChange?: TableProps<T>['onChange'];
  onPaginationChange?: (pagination: VindrTableProps['pagination']) => void;
  onRow?: TableProps<T>['onRow'];
  className?: string;
  selectedRowKeys?: string[];
  setSelectedRowKeys?: Dispatch<SetStateAction<VindrTableProps['selectedRowKeys']>> | undefined;
  wrapperClassName?: string | null | undefined;
  // dataSource?: { [key: string]: unknown }[];
  style?: React.CSSProperties;
  noDataText?: string;
  rowSelection?: TableProps<T>['rowSelection'];
}

export interface PaginationProps {
  page: number;
  size: 25 | 50 | 100 | number;
}

let clickTime = 0;
let mouseStartTime = 0;

const Table = ({
  columns,
  dataSource,
  scroll,
  rowKey = 'id',
  loading,
  pagination = {
    page: 1,
    size: 25,
  },
  contextMenu = false,
  count = 0,
  onTableChange,
  onPaginationChange,
  onRow,
  className = '',
  wrapperClassName = 'table-wrapper',
  style,
  noDataText = 'No Data',
  rowSelection,
  selectedRowKeys,
  setSelectedRowKeys,
  ...rest
}: VindrTableProps): JSX.Element => {
  const { theme } = useThemeContext();
  const [selectedRow, setSelectedRow] = useState(null);
  const [contextMenuState, setContextMenuState] = useState({
    currentRecord: null,
    selectedRecords: [],
    visible: false,
    x: 0,
    y: 0,
  });
  const { t } = useTranslation('Vindoc');
  const contextMenuProps = contextMenu
    ? {
        ...contextMenu,
        onClick: (it) => contextMenu.onClick(it, contextMenuState.currentRecord),
      }
    : false;

  const handlePaginationChange = useCallback(
    (page, pageSize) => {
      onPaginationChange({ page, size: pageSize });
    },
    [onPaginationChange]
  );

  const { scroll: calcTableScroll } = useCalcTableScroll({
    className: wrapperClassName,
  });

  const onClickOutside = useCallback(() => {
    setContextMenuState({ ...contextMenuState, visible: false });
    document.removeEventListener(`click`, onClickOutside);
  }, [contextMenuState]);

  const handleContextMenu = useCallback(
    ({ event, record }) => {
      event.stopPropagation();
      event.preventDefault();
      setSelectedRow(record);

      // Filter selected row keys
      setSelectedRowKeys?.([record]); //? Only select only current row when right-click

      // Close context menu on outside click
      if (!contextMenuState.visible) {
        document.addEventListener(`click`, onClickOutside);
      }

      // Set context menu state
      setContextMenuState({
        visible: true,
        x: event.clientX || event?.changedTouches?.[0].clientX,
        y: event.clientY || event?.changedTouches?.[0].clientY,
        currentRecord: record,
        selectedRecords: [record],
      });
    },
    [contextMenuState.visible, onClickOutside, setSelectedRowKeys]
  );

  const handleTouchEnd = useCallback(({ event, onContextMenu }) => {
    const mouseEndTime = new Date().getTime();

    // Open context menu on long touch
    if (onContextMenu && mouseEndTime - mouseStartTime >= 500) {
      onContextMenu(event);
    }
  }, []);

  const handleClick = useCallback(
    ({ event, record }) => {
      const time = new Date().getTime();

      if (
        record === selectedRow &&
        time - clickTime <= 500 &&
        isIOS() &&
        onRow(record)?.onDoubleClick
      ) {
        onRow(record)?.onDoubleClick(event);
      }
      clickTime = time;
      setSelectedRow(record);
      onRow?.(record)?.onClick?.(event);
    },
    [onRow, selectedRow]
  );

  const handleTouchStart = () => {
    mouseStartTime = new Date().getTime();
  };

  return (
    <>
      <div className={twMerge(`h-0 flex-1 overflow-hidden`, wrapperClassName)}>
        <ConfigProvider
          theme={{
            token: {
              colorBgContainer: theme === 'dark' ? '#2c5166' : '#eaeaea',
            },
          }}
        >
          <AntTable
            className={twMerge('', className)}
            size="small"
            showSorterTooltip={false}
            {...{ columns, dataSource, rowSelection, rowKey, loading, style }}
            scroll={{
              y: scroll?.y || calcTableScroll?.y,
              x: scroll?.x || 'none',
            }}
            rowClassName={(record) => (record === selectedRow ? 'selected' : '')}
            pagination={false}
            onChange={onTableChange}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(noDataText)} />,
            }}
            onRow={(record) => {
              if (!(onRow || contextMenu)) {
                return { onClick: () => setSelectedRow(record) };
              }

              const onContextMenu = contextMenu
                ? (event) => handleContextMenu({ event, record })
                : undefined;

              return {
                ...onRow?.(record),
                onClick: (event) => handleClick({ event, record }),
                ...(isIOS()
                  ? {
                      onTouchStart: handleTouchStart,
                      onTouchEnd: (event) => handleTouchEnd({ event, onContextMenu }),
                    }
                  : { onContextMenu }),
              };
            }}
            {...rest}
          />
        </ConfigProvider>
      </div>
      {contextMenu && contextMenuProps && contextMenuProps?.items?.length ? (
        <PopupContextMenu
          menu={contextMenuProps}
          {...contextMenuState}
          setContextMenuState={setContextMenuState}
        />
      ) : null}
      {pagination && (
        <div className="mt-3 flex w-full justify-end">
          <PaginationTable
            page={pagination?.page}
            size={pagination?.size}
            onChange={handlePaginationChange}
            totalElements={count}
          />
        </div>
      )}
    </>
  );
};

export default memo(Table, (prevProps, nextProps) => {
  return (
    isEqual(prevProps.dataSource, nextProps.dataSource) &&
    isEqual(prevProps.loading, nextProps.loading) &&
    isEqual(prevProps.pagination, nextProps.pagination) &&
    isEqual(prevProps.scroll, nextProps.scroll) &&
    isEqual(prevProps.columns, nextProps.columns) &&
    isEqual(prevProps.count, nextProps.count) &&
    isEqual(prevProps.contextMenu, nextProps.contextMenu) &&
    isEqual(prevProps.noDataText, nextProps.noDataText) &&
    isEqual(prevProps.components, nextProps.components) &&
    isEqual(prevProps.rowSelection, nextProps.rowSelection)
  );
});
